import React, { useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Portal, Button, Icon } from 'components/atoms';
import styled from 'styled-components/macro';
import { transparentize } from 'polished';
import { useOnClickOutside } from 'hooks';
import { above } from 'services/breakpoints';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { modifiersType } from 'types';
import { useKeyPressEvent } from 'react-use';
import { useAppContext, actions } from 'context/AppContext'
import { generateKey } from 'services/generators'

const MODAL_MODIFIERS = {
  stretch: () => `
    padding: 0 !important;
  `,
};

const ModalBase = ({
  children, toggle, isOpen, modifiers, onClickOutside, disableClickOutside
}) => {
  const containerEl = useRef(null);
  const [{ modalsOpen, shouldCloseModalOnClickOutside }, dispatch] = useAppContext();

  const modalKey = useMemo(() => generateKey('modal'), [])
  // only close on click outside if it's the last opened modal
  useOnClickOutside(containerEl, () => {
    const isLastModalOpened = modalKey === modalsOpen[modalsOpen.length - 1]
    // modal will never close on click outside due to disableClickOutside being undefined
    // if in the future you want to activate this feature, simply add ! before disableClickOutside
    if (isLastModalOpened && shouldCloseModalOnClickOutside && disableClickOutside) {
      onClickOutside()
      toggle()
    }
  });


  useKeyPressEvent('Escape', () => {
    const isBodyFocused = document.activeElement.tagName.toLowerCase() === 'body'
    const isLastModalOpened = modalKey === modalsOpen[modalsOpen.length - 1]
    if (isOpen && isLastModalOpened && isBodyFocused) toggle()
  });

  useEffect(() => {
    if (modalKey) {
      dispatch({
        type: isOpen ? actions.MODAL_ADD : actions.MODAL_REMOVE,
        data: modalKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalKey])

  return (
    <Portal>
      {isOpen && (
        <Modal>
          <Modal.Container ref={containerEl} modifiers={modifiers}>
            <Modal.Close>
              <Button modifiers={['invisibleDarker', 'smaller']} onClick={toggle}>
                <Icon icon="fal fa-times" height={14} />
              </Button>
            </Modal.Close>
            {children}
          </Modal.Container>
        </Modal>
      )}
    </Portal>
  );
};

const Modal = styled.div`
  position: fixed;
  overflow-y: scroll;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  background-color: ${props => transparentize(0.78, props.theme.colors.bgDark)};
  padding: ${props => props.theme.spacing.s};
  padding-top: ${props => props.theme.spacing.xl2};
  padding-bottom: ${props => props.theme.spacing.xl2};
  ${above.md`
    padding-top: ${props => props.theme.spacing.xl6};
    padding-bottom: ${props => props.theme.spacing.xl6};
  `}
`;

Modal.Container = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
  max-width: 786px;
  background-color: ${props => props.theme.colors.bg};
  padding: ${props => props.theme.spacing.xl}
    ${props => props.theme.spacing.xl2};
  box-shadow: ${props => props.theme.shadows.card};
  ${above.md`
    min-width: 640px;
  `}
  ${applyStyleModifiers(MODAL_MODIFIERS)};
`;

Modal.Close = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  transform: translateX(-100%);
  padding: ${props => props.theme.spacing.xs2};
  z-index: ${props => props.theme.values.zIndex.superBig};
`

ModalBase.Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.xl3};
`;

ModalBase.Top = styled.div`
  padding-bottom: ${props => (props.smallSpacing ? props.theme.spacing.xs : props.theme.spacing.xl2)};
`


ModalBase.propTypes = {
  children: PropTypes.node.isRequired,
  toggle: PropTypes.func,
  onClickOutside: PropTypes.func,
  isOpen: PropTypes.bool,
  modifiers: modifiersType,
  disableClickOutside: PropTypes.bool

};

ModalBase.defaultProps = {
  toggle: () => null,
  onClickOutside: () => null,
  isOpen: false,
  modifiers: [],
  disableClickOutside: false
};

export default ModalBase;
